import { useRef, useEffect, useCallback } from 'react'
import {
  MessageContainer,
  type ConversationMessage,
} from '@pipecat-ai/voice-ui-kit'
import { useTranslation } from 'react-i18next'
import type { VapiMessage } from './useVoiceClient'

function toVoiceKitMessage(msg: VapiMessage): ConversationMessage {
  const createdAt = msg.timestamp || new Date().toISOString()
  if (msg.role === 'function_call') {
    return {
      role: 'function_call',
      final: msg.final,
      parts: [{ text: msg.functionName ?? 'tool', final: msg.final, createdAt }],
      createdAt,
      functionCall: {
        function_name: msg.functionName ?? 'tool',
        args: {},
        result: msg.text || '',
        cancelled: false,
        status: 'completed',
      },
    }
  }
  return {
    role: msg.role === 'user' ? 'user' : 'assistant',
    final: msg.final,
    parts: [{ text: msg.text, final: msg.final, createdAt }],
    createdAt,
  }
}

interface Props {
  messages: VapiMessage[]
}

export function VapiConversationPanel({ messages }: Props) {
  const { t } = useTranslation('calls')
  const scrollRef = useRef<HTMLDivElement>(null)
  const userScrolledUp = useRef(false)

  const handleScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    userScrolledUp.current = el.scrollHeight - el.scrollTop - el.clientHeight > 80
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (el && !userScrolledUp.current) {
      el.scrollTop = el.scrollHeight
    }
  }, [messages])

  const vkMessages = messages.filter((m) => m.text || m.role === 'function_call').map(toVoiceKitMessage)

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-border bg-surface">
      <div className="border-b border-border px-4 py-2.5">
        <h3 className="text-sm font-semibold text-text-primary">{t('voiceTest.conversation.title')}</h3>
      </div>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto"
      >
        <div className="vkui-root dark">
          <div className="space-y-1 p-4">
            {vkMessages.length === 0 ? (
              <p className="py-8 text-center text-sm text-text-muted">
                {t('voiceTest.conversation.empty')}
              </p>
            ) : (
              vkMessages.map((msg, i) => (
                <MessageContainer
                  key={`${msg.role}-${msg.createdAt}-${i}`}
                  message={msg}
                  assistantLabel={t('voiceTest.conversation.agent')}
                  clientLabel={t('voiceTest.conversation.you')}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
